import type { ReactNode } from 'react'
import styles from './Pagination.module.css'

/**
 * Paging, by cursor.
 *
 * There is no page number here and there cannot be one, and that is the design
 * rather than a gap in it.
 *
 * An offset page asks the server for rows 25 to 49 of the current ordering. The
 * ordering is not stable: while somebody reads page two, a case is opened, an
 * escalation lands, a broadcast finishes sending. Everything shifts down by one.
 * The row that was number 25 is now number 26, so it appears again at the top of
 * page two, and the row that was number 50 has become number 49 and is never
 * shown at all. On a marketing table that is a cosmetic oddity. On a list of
 * people waiting for help it is a person nobody saw, and nothing on the screen
 * indicates that it happened.
 *
 * A cursor says "the rows after this one", so an insertion elsewhere in the set
 * changes nothing about what comes next. The price is that the set has no index:
 * "page 7 of 18" is a claim about positions that do not exist, so this component
 * shows a summary the caller composes instead, and offers previous and next.
 *
 * Both buttons stay focusable at the ends of the list. Disabling a button that
 * has focus hands focus back to the document body, so the keyboard user who
 * pressed Next until it stopped is silently returned to the top of the page.
 * `aria-disabled` says the same thing to assistive technology and keeps the
 * focus where the person left it.
 */

export interface CursorPage {
  hasPrevious: boolean
  hasNext: boolean
  /** The first row's cursor. Paging back asks for the rows before it. */
  startCursor: string | null
  /** The last row's cursor. Paging on asks for the rows after it. */
  endCursor: string | null
}

export interface PageSizeControl {
  value: number
  options: readonly number[]
  onChange(next: number): void
  /** Required, so the select cannot ship as an unnamed combobox. */
  label: string
}

export interface PaginationProps {
  page: CursorPage
  onNavigate(request: { direction: 'previous' | 'next'; cursor: string }): void
  /**
   * Names the nav landmark, so a page with two lists on it does not present two
   * regions both called "pagination".
   *
   * None of the copy props here have English defaults. A default is the reason
   * a console ships half translated: the strings someone remembered to pass are
   * translated and the ones that fell back are not, and nothing fails.
   */
  label: string
  previousLabel: string
  nextLabel: string
  /**
   * What is on screen, composed by the caller: "25 cases", "25 of about 412".
   * Approximation belongs in the wording, because an exact total costs a second
   * count query against a set that is changing anyway.
   */
  summary?: ReactNode
  pageSize?: PageSizeControl
  /** While a page is in flight. Both buttons decline rather than disappear. */
  busy?: boolean
  className?: string
}

export function Pagination({
  page,
  onNavigate,
  label,
  previousLabel,
  nextLabel,
  summary,
  pageSize,
  busy = false,
  className,
}: PaginationProps) {
  const previousBlocked = busy || !page.hasPrevious || page.startCursor === null
  const nextBlocked = busy || !page.hasNext || page.endCursor === null

  function goPrevious() {
    if (previousBlocked || page.startCursor === null) return
    onNavigate({ direction: 'previous', cursor: page.startCursor })
  }

  function goNext() {
    if (nextBlocked || page.endCursor === null) return
    onNavigate({ direction: 'next', cursor: page.endCursor })
  }

  return (
    <nav
      className={[styles.bar, className].filter(Boolean).join(' ')}
      aria-label={label}
      data-busy={busy || undefined}
    >
      {summary ? <p className={styles.summary}>{summary}</p> : null}

      <div className={styles.controls}>
        {pageSize ? (
          <label className={styles.size}>
            <span className={styles.sizeLabel}>{pageSize.label}</span>
            <select
              className={styles.select}
              value={pageSize.value}
              onChange={(event) => pageSize.onChange(Number(event.target.value))}
            >
              {pageSize.options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <button
          type="button"
          className={styles.step}
          onClick={goPrevious}
          aria-disabled={previousBlocked}
        >
          <StepGlyph direction="previous" />
          <span>{previousLabel}</span>
        </button>

        <button type="button" className={styles.step} onClick={goNext} aria-disabled={nextBlocked}>
          <span>{nextLabel}</span>
          <StepGlyph direction="next" />
        </button>
      </div>
    </nav>
  )
}

/**
 * Drawn here rather than taken from the shared icon set, for the same reason as
 * the table's sort arrow: two chevrons are not worth a dependency on a file the
 * data layer does not own. Hairline, 24 unit grid, round caps, no fill.
 */
function StepGlyph({ direction }: { direction: 'previous' | 'next' }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable={false}
    >
      {direction === 'previous' ? <path d="M14 6l-6 6 6 6" /> : <path d="M10 6l6 6-6 6" />}
    </svg>
  )
}
